<template>
  <v-container v-if="getBill" fluid>
    <AddPaymentMethod />

    <v-layout
      :row="$vuetify.breakpoint.name !== 'xs'"
      :reverse="$vuetify.breakpoint.name === 'xs'"
      :column="$vuetify.breakpoint.name === 'xs'"
      wrap
    >
      <v-flex md6 offset-md1 xs12>
        <checkout-form ref="paymentForm" />
      </v-flex>

      <v-flex md3 offset-md1 xs12>
        <bill />
      </v-flex>
    </v-layout>

    <v-dialog v-model="confirmOrder" max-width="380">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ $vuetify.t('$vuetify.checkout.title') }}
        </v-card-title>

        <v-card-text style="text-align: left">
          <div v-if="!isDineIn && getCheckoutInfo.scheduledTime !== 'now'" class="stripe">
            <b>{{ $vuetify.t('$vuetify.checkout.confirmDialog.scheduled') }}</b> <br>

            <div>
              <span v-if="new Date(getCheckoutInfo.scheduledDay).getDate() != new Date().getDate()">
                {{_formatDate(getCheckoutInfo.scheduledDay)}},
              </span>
              {{_formatTime(getCheckoutInfo.scheduledTime)}}
            </div>
          </div>
          <div v-else-if="isDineInTableSet" class="stripe">
            <b>Table</b> <br>

            <div>
              {{ getTableDisplay }}
            </div>
          </div>

          <div class="stripe">
            <b>{{ $vuetify.t('$vuetify.checkout.confirmDialog.name') }}</b> <br>

            <div>
              {{getStore.name}}
            </div>
          </div>

          <div v-if="!isDineIn" class="stripe">
            <b>{{ $vuetify.t('$vuetify.checkout.confirmDialog.location') }}</b> <br>

            <div>
              {{getStore.address}}<span v-if="getStore.city">, {{getStore.city}} </span>
            </div>
          </div>

          <div v-if="isDelivery" class="stripe">
            <b>{{ $vuetify.t('$vuetify.checkout.confirmDialog.deliveryInfo') }}</b> <br>

            <div>
              <div style="padding-bottom: 7.5px;">
                {{ getCheckoutInfo.phoneNumber | phone }}
              </div>
              {{ getDeliveryAddressFormatted }}
            </div>
          </div>
        </v-card-text>

        <v-card-text style="font-size: 16px; text-align: left; padding-bottom: 5px">
          {{ checkoutText }}
        </v-card-text>

        <v-card-actions style="padding-bottom: 20px">
          <v-layout row align-start>
            <v-flex xs5>
              <v-btn
                :disabled="isCheckoutLoading"
                color="normal"
                text
                outline
                style="width: 90%; height: 42px; margin: auto;"
                @click="confirmOrder = false"
              >
                {{ $vuetify.t('$vuetify.checkout.confirmCancel') }}
              </v-btn>
            </v-flex>

            <v-flex xs7>
              <v-btn
                :disabled="isCheckoutLoading"
                :loading="isCheckoutLoading"
                color="primary"
                text
                style="width: 90%; height: 42px; margin: auto;"
                @click="submitOrder"
              >
                {{ isOnlyOfflinePay ? $vuetify.t('$vuetify.account.offline') : $vuetify.t('$vuetify.checkout.confirmDialog.pay') }}
                <!-- Pay in person in french is too long to fit price -->
                <br v-if="isOnlyOfflinePay && $vuetify.lang.current == 'fr'">
                ({{ getTotal | currency }})
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import get from 'lodash/get';

  import { mapGetters, mapActions } from 'vuex';
  import { formatDate, formatTime, matchTranslation } from "@/services/util.service"

  import AddPaymentMethod from '@/components/Account/AddPaymentMethod.vue'
  import CheckoutForm from '@/components/Checkout/CheckoutForm'
  import Bill from '@/components/Checkout/Bill'
import { isNullOrEmpty } from '../services/util.service';

  export default {
    name: 'checkout',
    components: { AddPaymentMethod, CheckoutForm, Bill },

    data: () => ({
      confirmOrder: false,
      isCheckoutLoading: false
    }),

    computed: {
      checkoutText() {
        const _translations = get(this.getStore, 'settings.cpSettings.checkoutDisclaimer.translations', [])

        return matchTranslation(_translations) || this.$vuetify.t('$vuetify.checkout.description')
      },

      getDeliveryAddressFormatted() {
        return get(this.getDeliveryAddressResult, 'formatted_address', '')
      },

      // We use different wording for only offline pay since they aren't "paying now"
      isOnlyOfflinePay() {
        return !isNullOrEmpty(this.getPaymentType) && this.getPaymentType.length == 1 && this.getPaymentType[0] == 'offline'
      },

      ...mapGetters([
        'isLoggedIn',
        'getStore',
        'getBill',
        'getCheckoutInfo',
        'getTotal',
        'isDineIn',
        'isDelivery',
        'isDineInTableSet',
        'getTableDisplay',
        'getDeliveryAddress',
        'getDeliveryAddressResult',
        'getPaymentType'
      ])
    },

    methods: {
      _formatDate: dt => !!dt ? formatDate(dt.id || dt) : null,
      _formatTime: dt => !!dt ? formatTime(dt.id || dt) : null,

      submitOrder(payByMembership) {
        this.isCheckoutLoading = true

        const checkoutInfo = {...this.getCheckoutInfo}

        // Only include the email if the user stated they want an emal receipt
        if (!checkoutInfo.emailReceipt && checkoutInfo.email)
          delete checkoutInfo.email

        return this.$validator.validate().then(async valid => {
          const cancelLoading = () => {
              // Hide confirm no matter what
              this.confirmOrder = false
              this.isCheckoutLoading = false
          }

          if (valid) {
            let paymentAction
             //This getTotal check is if the user has a 0 total(or using free coupons/items) and is using Apple devices
             if (this.getPaymentType.includes('ApplePay') && !(this.getTotal === 0)) {
              paymentAction = this.$refs.paymentForm.selectApplePay
            } else {
              paymentAction = this.payOrder
            }

            paymentAction(checkoutInfo).then(() => {
              cancelLoading()
            }).catch(() => {
              cancelLoading()
            })
          } else {
            cancelLoading()
          }
        })
      },

      ...mapActions([
        'payOrder',
        'fetchBillView',
        'setDefaultCheckoutInfoIfEmpty',
        'setPaymentType',
        'fetchAccountDetails',
        'showSnack'
      ])
    },

    mounted() {
      window.scrollTo(0, 0)
      this.setDefaultCheckoutInfoIfEmpty()

      this.fetchBillView()

      // Account details are required for membership points
      // Replace storeId with route when routes are updated
      if (this.isLoggedIn) {
        this.fetchAccountDetails(this.$route.params.sid)

        this.$api.get('/customer').then(res => {
          this.SET_CUSTOMER(res.data)
        }).catch(e => {
          this.showSnack({
            text: get(e, 'response.data.errorMsg', ''),
            color: 'error'
          })
        })
      }
    }
  }
</script>

<style scoped>
  .stripe {
    padding: 15px 5px;
  }
  .stripe b {
    font-size: 12px;
    color: grey !important;
  }
  .stripe:not(b) {
    font-size: 18px;
    line-height: 22px;
  }
</style>
